import React, { useCallback } from "react";
import { useField } from "formik";
import { v4 as uuid } from "uuid";
import { TransferRuleDirection } from "@common/types/transactionRule";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { Button } from "@design-system/Button/Button";
import { PlusIcon } from "@icons";
import styles from "../TransferRuleForm.module.scss";
import {
    FormRuleActionType,
    TransactionRuleActionFormSchema,
} from "../../TransactionRuleForm/constants";
import { TransferRuleFormActions } from "../constants";
import { TransferRuleActionForm } from "./TransferRuleActionForm";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface Props {
    transferDirection: TransferRuleDirection;
    disabled?: boolean;
}

export const TransferRuleActionsForm: React.FC<Props> = ({
    transferDirection,
    disabled,
}) => {
    const { enabled: isGlFirst } = useIsGlFirst();
    const [{ value: actions }, , { setValue: setActions }] = useField<
        TransactionRuleActionFormSchema[]
    >(`actions.${transferDirection}`);

    const addAction = useCallback(() => {
        void setActions([...actions, { id: uuid(), transferDirection }]);
    }, [actions, setActions, transferDirection]);

    const removeAction = useCallback(
        (index: number) => {
            void setActions(actions.filter((_, i) => i !== index));
        },
        [actions, setActions],
    );

    // GL-first workspaces cannot add new category actions, so only the
    // account action slot is available unless a pre-existing rule already
    // carries a category action.
    const hasCategoryAction = actions.some(
        (action) => action.actionType === FormRuleActionType.CATEGORY,
    );
    const maxActionsCount =
        isGlFirst && !hasCategoryAction ? 1 : TransferRuleFormActions.length;
    const canAddAction = actions.length < maxActionsCount;

    return (
        <section
            className={styles.actions}
            style={
                disabled ? { opacity: 0.5, pointerEvents: "none" } : undefined
            }
        >
            <main className={styles.content}>
                <div className={styles.sectionRows}>
                    {actions.map((action, index) => (
                        <div className={styles.sectionRow} key={action.id}>
                            <TransferRuleActionForm
                                transferDirection={transferDirection}
                                index={index}
                                onRemove={removeAction}
                            />
                        </div>
                    ))}
                    <div className={styles.sectionRow}>
                        <Tooltip
                            content={
                                canAddAction
                                    ? undefined
                                    : "You can add only one action per type"
                            }
                        >
                            <Button
                                variant="primary"
                                size="md"
                                leftIcon={<PlusIcon />}
                                onClick={addAction}
                                type="button"
                                disabled={!canAddAction}
                            >
                                Add
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </main>
        </section>
    );
};
